import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Clock, ShieldCheck, RefreshCw, LogOut, ArrowRight, MailQuestion, XCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";

export default function ExpertPending() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout, refresh } = useAuth();

  const status = user?.expert_status || "pending";
  const infoRequested = user?.expert_info_requested;
  const rejected = status === "rejected";

  const handleLogout = async () => { await logout(); navigate("/login"); };
  const recheck = async () => {
    const u = await refresh();
    if (u?.role === "expert" && u?.expert_status === "approved") navigate("/expert");
  };

  const icon = rejected ? XCircle : infoRequested ? MailQuestion : Clock;
  const Icon = icon;
  const tone = rejected ? "bg-destructive/10 text-destructive" : "bg-warning/10 text-warning";

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="h-16 flex items-center justify-between px-5 sm:px-8 border-b border-border">
        <Logo />
        <div className="flex items-center gap-2">
          <LanguageSwitcher variant="outline" />
          <Button variant="ghost" size="sm" data-testid="pending-logout" onClick={handleLogout} className="rounded-lg gap-1.5 text-muted-foreground">
            <LogOut size={15} /> {t("common.signOut")}
          </Button>
        </div>
      </header>

      <main className="flex-1 grid place-items-center px-5 py-12">
        <div className="w-full max-w-md text-center animate-fade-up" data-testid="expert-pending">
          <span className={`mx-auto grid place-items-center w-16 h-16 rounded-full mb-6 ${tone}`}>
            <Icon size={30} strokeWidth={1.6} />
          </span>
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-primary mb-2">{t("expert.pending.eyebrow")}</p>
          <h1 className="text-2xl font-semibold tracking-tight">
            {rejected ? t("expert.pending.rejectedTitle") : infoRequested ? t("expert.pending.infoTitle") : t("expert.pending.title")}
          </h1>
          <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
            {rejected ? t("expert.pending.rejectedBody") : infoRequested ? t("expert.pending.infoBody") : t("expert.pending.body")}
          </p>

          {!rejected && (
            <div className="mt-7 rounded-xl border border-border bg-secondary/30 p-4 text-left">
              <div className="flex items-center gap-2.5">
                <span className="grid place-items-center w-9 h-9 rounded-lg bg-primary/10 text-primary shrink-0"><ShieldCheck size={17} strokeWidth={1.75} /></span>
                <div>
                  <p className="text-sm font-medium">{t("expert.pending.stepsTitle")}</p>
                  <p className="text-xs text-muted-foreground">{t("expert.pending.stepsSub")}</p>
                </div>
              </div>
            </div>
          )}

          <div className="mt-7 flex flex-col gap-2.5">
            <Button data-testid="pending-edit-profile" onClick={() => navigate("/expert/profile")} className="h-11 rounded-lg gap-1.5">
              {t("expert.pending.editProfile")} <ArrowRight size={16} />
            </Button>
            <Button variant="outline" data-testid="pending-recheck" onClick={recheck} className="h-11 rounded-lg gap-1.5 bg-card">
              <RefreshCw size={15} /> {t("expert.pending.checkAgain")}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
